import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import { Calculate } from '@mui/icons-material';
import { BudgetApiService } from '../../services/budgetApiService';

interface BudgetOption {
  id: number;
  proyecto: string;
  cliente: string;
  display: string;
}

const CalculadoraH: React.FC = () => {
  // Estados
  const [budgets, setBudgets] = useState<BudgetOption[]>([]);
  const [selectedBudget, setSelectedBudget] = useState<BudgetOption | null>(null);
  const [totalUSD, setTotalUSD] = useState<number>(0);
  const [porcentajeFacturado, setPorcentajeFacturado] = useState<number>(50);
  const [dolarActual, setDolarActual] = useState<number>(1300);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>('');

  // Cargar presupuestos con búsqueda server-side
  useEffect(() => {
    const loadBudgets = async () => {
      try {
        setLoading(true);
        // Si hay texto de búsqueda, buscar en el servidor; sino traer los últimos 100
        const response = await BudgetApiService.listBudgets({
          search: inputValue || undefined,
          limit: 100
        });
        
        const budgetOptions: BudgetOption[] = response.items.map((budget: any) => ({
          id: budget.id,
          proyecto: budget.proyecto,
          cliente: budget.cliente,
          display: `${budget.proyecto} - ${budget.cliente}`
        }));
        
        setBudgets(budgetOptions);
      } catch (err) {
        setError('Error al cargar los presupuestos');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    // Debounce: esperar 300ms después de que el usuario deje de escribir
    const timeoutId = setTimeout(loadBudgets, 300);
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  // Cuando se selecciona un presupuesto
  const handleBudgetSelect = async (budget: BudgetOption | null) => {
    setSelectedBudget(budget);
    setError(null);

    if (!budget) {
      setTotalUSD(0);
      return;
    }

    try {
      setLoading(true);
      // Obtener el presupuesto completo
      const budgetData = await BudgetApiService.getBudget(budget.id);
      
      // Obtener versiones
      const versionsData = await BudgetApiService.getBudgetVersions(budget.id);
      
      if (versionsData && versionsData.length > 0) {
        // Seleccionar siempre la última versión (mayor version_number)
        // El backend devuelve las versiones ordenadas por version_number DESC
        const currentVersionData: any = versionsData[0];
        
        if (currentVersionData) {
          // Obtener la versión con detalles
          const versionWithDetails = await BudgetApiService.getBudgetVersion(budget.id, currentVersionData.id);
          
          // Usar total_materiales_usd que YA tiene los descuentos por ambiente aplicados
          // Este valor se calcula en el backend con calculate_version_totals
          let totalUSD = parseFloat(String(versionWithDetails.total_materiales_usd || 0));
          
          // Si no hay total_materiales_usd, calcular desde los ambientes aplicando descuentos por ambiente
          if (totalUSD === 0 && versionWithDetails.environments && versionWithDetails.environments.length > 0) {
            versionWithDetails.environments.forEach((env: any) => {
              let envTotalUSD = 0;
              
              if (env.materials && env.materials.length > 0) {
                env.materials.forEach((mat: any) => {
                  // Solo contar materiales en USD
                  if (mat.currency === 'USD' || !mat.currency) {
                    const monto = mat.monto || (mat.unidades * mat.precio_unitario);
                    envTotalUSD += parseFloat(String(monto));
                  }
                });
              }
              
              // Aplicar descuento del ambiente si existe
              const descuentoAmbiente = env.descuento_materiales || 0;
              if (descuentoAmbiente > 0) {
                envTotalUSD = envTotalUSD * (1 - descuentoAmbiente / 100);
              }
              
              totalUSD += envTotalUSD;
            });
          }
          
          setTotalUSD(Math.max(0, totalUSD));
        } else {
          setError('No se encontró la versión actual del presupuesto');
        }
      } else {
        setError('No se encontraron versiones del presupuesto');
      }
    } catch (err) {
      setError('Error al obtener los datos del presupuesto');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Cálculos
  const porcentajeH = 100 - porcentajeFacturado;
  
  // Valores en USD
  const facturadoUSD = (totalUSD * porcentajeFacturado) / 100;
  const ivaFacturadoUSD = facturadoUSD * 0.21;
  const facturadoConIvaUSD = facturadoUSD + ivaFacturadoUSD;
  const hUSD = (totalUSD * porcentajeH) / 100;
  const totalConIvaUSD = facturadoConIvaUSD + hUSD;

  // Valores en ARS
  const facturadoARS = facturadoUSD * dolarActual;
  const ivaFacturadoARS = facturadoARS * 0.21;
  const facturadoConIvaARS = facturadoARS + ivaFacturadoARS;
  const hARS = hUSD * dolarActual;
  const totalConIvaARS = facturadoConIvaARS + hARS;

  const formatCurrency = (value: number, currency: 'USD' | 'ARS' = 'USD'): string => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Calculate color="primary" />
            Calculadora H
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Herramienta para calcular el split de facturación y conversión a pesos
          </Typography>
        </Box>

        {/* Errores */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Cards de Selección y Configuración */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {/* Card de Selección */}
          <Card>
            <CardHeader title="Seleccionar Presupuesto" />
          <CardContent>
            <Stack spacing={2}>
              <Autocomplete
                options={budgets}
                getOptionLabel={(option) => option.display}
                value={selectedBudget}
                onChange={(event, newValue) => handleBudgetSelect(newValue)}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                loading={loading}
                filterOptions={(x) => x} // Desactivar filtro local, usamos búsqueda server-side
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar presupuesto"
                    placeholder="Escribe proyecto o cliente..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />

              {selectedBudget && (
                <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total del presupuesto
                  </Typography>
                  <Typography variant="h6" color="success.main" fontWeight="bold">
                    {formatCurrency(totalUSD, 'USD')}
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
          </Card>

          {/* Card de Configuración */}
          {selectedBudget && (
            <Card>
              <CardHeader title="Configurar Split y Tipo de Cambio" />
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  {/* Porcentaje Facturado */}
                  <Box>
                    <TextField
                      fullWidth
                      label="% Facturado"
                      type="number"
                      value={porcentajeFacturado}
                      onChange={(e) => {
                        const value = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                        setPorcentajeFacturado(value);
                      }}
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {porcentajeFacturado.toFixed(2)}%
                    </Typography>
                  </Box>

                  {/* Porcentaje H (automático) */}
                  <Box>
                    <TextField
                      fullWidth
                      label="% Sin Factura"
                      type="number"
                      value={porcentajeH}
                      disabled
                      InputProps={{ readOnly: true }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {porcentajeH.toFixed(2)}% (automático)
                    </Typography>
                  </Box>
                </Box>

                {/* Dólar Actual */}
                <TextField
                  fullWidth
                  label="Dólar Actual"
                  type="number"
                  value={dolarActual}
                  onChange={(e) => setDolarActual(parseFloat(e.target.value) || 1300)}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Tipo de cambio para conversión a ARS"
                />
              </Stack>
            </CardContent>
            </Card>
          )}
        </Box>

        {/* Tabla USD */}
        {selectedBudget && (
          <Card>
            <CardHeader title="Resultados en USD" />
            <CardContent>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Concepto</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Monto USD</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>% del Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Facturado ({porcentajeFacturado.toFixed(2)}%)</TableCell>
                      <TableCell align="right">{formatCurrency(facturadoUSD, 'USD')}</TableCell>
                      <TableCell align="right">{porcentajeFacturado.toFixed(2)}%</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>IVA Facturado (21%)</TableCell>
                      <TableCell align="right">{formatCurrency(ivaFacturadoUSD, 'USD')}</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: 'info.light' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Facturado + IVA</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(facturadoConIvaUSD, 'USD')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Sin Factura ({porcentajeH.toFixed(2)}%)</TableCell>
                      <TableCell align="right">{formatCurrency(hUSD, 'USD')}</TableCell>
                      <TableCell align="right">{porcentajeH.toFixed(2)}%</TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: 'success.light' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(totalConIvaUSD, 'USD')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Tabla ARS */}
        {selectedBudget && (
          <Card>
            <CardHeader title={`Resultados en ARS (Dólar: $${dolarActual.toFixed(2)})`} />
            <CardContent>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Concepto</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Monto ARS</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>% del Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Facturado ({porcentajeFacturado.toFixed(2)}%)</TableCell>
                      <TableCell align="right">{formatCurrency(facturadoARS, 'ARS')}</TableCell>
                      <TableCell align="right">{porcentajeFacturado.toFixed(2)}%</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>IVA Facturado (21%)</TableCell>
                      <TableCell align="right">{formatCurrency(ivaFacturadoARS, 'ARS')}</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: 'info.light' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Facturado + IVA</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(facturadoConIvaARS, 'ARS')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Sin Factura ({porcentajeH.toFixed(2)}%)</TableCell>
                      <TableCell align="right">{formatCurrency(hARS, 'ARS')}</TableCell>
                      <TableCell align="right">{porcentajeH.toFixed(2)}%</TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: 'success.light' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(totalConIvaARS, 'ARS')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
};

export default CalculadoraH;
